import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import MDTypography from 'components/MDTypography/index';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { SeverityColor, SeverityIcon } from 'types/severity';

const getDateRangeParts = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const pad = num => num.toString().padStart(2, '0');

  const startHours = pad(startDate.getUTCHours());
  const startMinutes = pad(startDate.getUTCMinutes());
  const startDay = pad(startDate.getUTCDate());
  const startMonth = pad(startDate.getUTCMonth() + 1);
  const endHours = pad(endDate.getUTCHours());
  const endMinutes = pad(endDate.getUTCMinutes());
  const endDay = pad(endDate.getUTCDate());
  const endMonth = pad(endDate.getUTCMonth() + 1);
  const isSameDate = startDay === endDay && startMonth === endMonth && startDate.getUTCFullYear() === endDate.getUTCFullYear();

  return {
    startTime: `${startHours}:${startMinutes}`,
    endTime: `${endHours}:${endMinutes}`,
    startDate: `${startDay}.${startMonth}`,
    endDate: `${endDay}.${endMonth}`,
    isSameDate,
  };
};

const DateRange = ({ range }) => {
  const { startTime, endTime, startDate, endDate, isSameDate } = getDateRangeParts(range.start, range.end);

  if (isSameDate) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
        {`${startTime} - ${endTime} (${startDate})`}
      </Box>
    );
  }

  return `${startTime} (${startDate}) - ${endTime} (${endDate})`;
};

DateRange.propTypes = {
  range: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }).isRequired,
};

const groupRangesByDate = ranges => {
  return ranges.reduce((acc, range) => {
    const rangeParts = getDateRangeParts(range.start, range.end);
    const groupKey = rangeParts.isSameDate
      ? rangeParts.startDate
      : `${rangeParts.startTime}-${rangeParts.endTime}-${rangeParts.startDate}-${rangeParts.endDate}-${range.start_unix}`;
    const existingGroup = acc.find(group => group.key === groupKey);
    const displayTime = rangeParts.isSameDate
      ? `${rangeParts.startTime} - ${rangeParts.endTime}`
      : `${rangeParts.startTime} (${rangeParts.startDate}) - ${rangeParts.endTime} (${rangeParts.endDate})`;

    if (existingGroup) {
      existingGroup.ranges.push({ ...range, displayTime });
      return acc;
    }

    acc.push({
      key: groupKey,
      dateLabel: rangeParts.isSameDate ? rangeParts.startDate : null,
      ranges: [{ ...range, displayTime }],
    });

    return acc;
  }, []);
};

const ControllerAlert = props => {
  const [expanded, setExpanded] = useState(false);
  const showSensorId = props.showSensorId === undefined || props.showSensorId === true;
  const alertTimes = Array.isArray(props.alertTimes) ? props.alertTimes : [];
  const expandedRows = groupRangesByDate(alertTimes);

  const getPaperIcon = () => {
    const alertSeverity = {
      'Filter Dirty Alarm': 0,
      'Compressor Fault': 0,
      'Fan Fault': 0,
      'Sensor Fault': 0,
      'High Temp Alarm': 0,
      'Low Temp Alarm': 0,
      'Communication Fault': 0,
      'Mechanical / actuator fault': 0,
      'Cooling may still pass when valve should be shut': 0,
      'Valve may be stuck closed or not responding': 0,
      'Valve feedback or control may be frozen': 0,
    };

    const severityIndex = alertSeverity[props.description] !== undefined ? alertSeverity[props.description] : 2;

    return (
      <Paper
        color="white"
        className="alertIcon"
        elevation={0}
        style={{
          background: `linear-gradient(160deg, ${SeverityColor[severityIndex]?.[0]}, ${SeverityColor[severityIndex]?.[1]}`,
        }}
        sx={{
          display: 'grid',
          placeItems: 'center',
          paddingBottom: 0,
          width: '2.5rem',
          height: '2.5rem',
          boxShadow: 'none',
          border: 'none',
          lineHeight: 0,
          '& .MuiSvgIcon-root': {
            display: 'block',
            width: '2rem',
            height: '2rem',
            margin: 0,
          },
          '& svg': {
            display: 'block',
          },
        }}>
        {SeverityIcon[1]}
      </Paper>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        marginBottom: '.7rem',
        borderRadius: '.5rem',
        boxShadow: 'none',
        border: 'none',
        overflow: 'hidden',
      }}>
      <Grid container py={0.8} px={1} sx={{ minHeight: '4rem' }} alignItems="center">
        <Grid item xs={1.5} md={1.2}>
          {getPaperIcon()}
        </Grid>
        <Grid item xs={10.4} md={10.8}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {showSensorId && (
              <Grid item>
                <MDTypography fontSize={14} fontWeight="bold" color="inherit" textAlign="start">
                  {props.sensorId ?? ''}
                </MDTypography>
              </Grid>
            )}
            <Grid
              item
              xs={7}
              md={10}
              sx={{
                display: 'flex',
                padding: '.3rem',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: '100%',
                width: '100%',
              }}>
              <MDTypography variant="body2" color="inherit" fontSize={16}>
                {props.description ?? ''}
              </MDTypography>
              {alertTimes.length !== 1 ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MDTypography variant="body2" color="inherit" fontSize={24} mx={1}>
                    {props.alertsNum}
                  </MDTypography>
                  <AccessTimeIcon sx={{ scale: '1.2' }} mt={0.5} />
                  <IconButton fontSize="medium" style={{ color: 'black' }} onClick={() => setExpanded(value => !value)}>
                    <ExpandMoreIcon
                      sx={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 180ms ease',
                      }}
                    />
                  </IconButton>
                </Box>
              ) : (
                <MDTypography
                  fontSize={14}
                  sx={{ position: 'relative', whiteSpace: alertTimes[0] ? 'nowrap' : 'normal' }}
                  color="inherit"
                  textAlign="center"
                  width="fit-content">
                  {alertTimes[0] ? <DateRange range={alertTimes[0]} /> : ''}
                </MDTypography>
              )}
            </Grid>
          </Stack>
        </Grid>
      </Grid>
      {alertTimes.length !== 1 && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 1.5 }}>
            {expandedRows.map((row, index) => (
              <Box
                key={row.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontSize: 14,
                  padding: '8px 12px',
                  color: '#000',
                  backgroundColor: index % 2 === 0 ? '#f7f8fb' : '#fff',
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                }}>
                {row.dateLabel && (
                  <Typography component="span" sx={{ minWidth: '3.5rem', fontSize: 14, fontWeight: 600, color: '#000' }}>
                    {row.dateLabel}
                  </Typography>
                )}
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, whiteSpace: 'nowrap' }}>
                  {row.ranges.map((rangeObj, rangeIndex) => (
                    <Typography
                      key={`${rangeObj.start_unix}-${rangeIndex}`}
                      component="span"
                      sx={{ fontSize: 14, color: '#000', whiteSpace: 'nowrap' }}>
                      {rangeObj.displayTime}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};

ControllerAlert.propTypes = {
  description: PropTypes.string,
  sensorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showSensorId: PropTypes.bool,
  alertsNum: PropTypes.number,
  alertTimes: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      start_unix: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      end_unix: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      color: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
};

export default ControllerAlert;
